import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { HEALTH_STATE } from "contracts/enums/core";
import type { PageMapLens } from "../../enums/map";
import { PAGE_MAP_LENS } from "../../enums/map";
import type { PageSortKey, SortDirection } from "../../enums/scan";
import { PAGE_SORT_KEY, SORT_DIRECTION } from "../../enums/scan";
import { pageDisplayLabel } from "../pages/boundaries/page-label.parser";
import { isStrategicPage, issuesForPage } from "./scanExperienceModel";
import {
  type ClusterContext,
  clusterContextForPages,
  clusterKey,
  pageContentFitnessScore,
  pageHealthClass,
} from "./scanModel";

export const PAGE_SORT_OPTIONS: { value: PageSortKey; label: string }[] = [
  { value: PAGE_SORT_KEY.PRIORITY, label: "Priority" },
  { value: PAGE_SORT_KEY.SCORE_ASC, label: "Score: low to high" },
  { value: PAGE_SORT_KEY.SCORE_DESC, label: "Score: high to low" },
  { value: PAGE_SORT_KEY.ISSUES_DESC, label: "Issues: most first" },
  { value: PAGE_SORT_KEY.NAME, label: "Name: A-Z" },
  { value: PAGE_SORT_KEY.CLUSTER, label: "Cluster" },
];

export function isPageSortKey(value: string | null): value is PageSortKey {
  return PAGE_SORT_OPTIONS.some((option) => option.value === value);
}

export function sortPages(
  pages: OverviewResponse["pages"],
  sort: PageSortKey,
  data: OverviewResponse,
  lens: PageMapLens,
): OverviewResponse["pages"] {
  const clusterContext = clusterContextForPages(data.pages);
  return [...pages].sort((left, right) => {
    const compared = comparePages(
      left,
      right,
      sort,
      data,
      lens,
      clusterContext,
    );
    if (compared !== 0) {
      return compared;
    }
    return pageDisplayLabel(left, data.site).localeCompare(
      pageDisplayLabel(right, data.site),
      undefined,
      { sensitivity: "base" },
    );
  });
}

function comparePages(
  left: OverviewResponse["pages"][number],
  right: OverviewResponse["pages"][number],
  sort: PageSortKey,
  data: OverviewResponse,
  lens: PageMapLens,
  clusterContext: ClusterContext,
): number {
  if (sort === PAGE_SORT_KEY.PRIORITY) {
    return (
      booleanFirst(isStrategicPage(data, left), isStrategicPage(data, right)) ||
      healthRank(activePageScore(left, lens)) -
        healthRank(activePageScore(right, lens)) ||
      issuesForPage(data, right).length - issuesForPage(data, left).length ||
      compareScores(
        activePageScore(left, lens),
        activePageScore(right, lens),
        SORT_DIRECTION.ASC,
      )
    );
  }
  if (sort === PAGE_SORT_KEY.SCORE_ASC) {
    return compareScores(
      activePageScore(left, lens),
      activePageScore(right, lens),
      SORT_DIRECTION.ASC,
    );
  }
  if (sort === PAGE_SORT_KEY.SCORE_DESC) {
    return compareScores(
      activePageScore(left, lens),
      activePageScore(right, lens),
      SORT_DIRECTION.DESC,
    );
  }
  if (sort === PAGE_SORT_KEY.ISSUES_DESC) {
    return (
      issuesForPage(data, right).length - issuesForPage(data, left).length ||
      compareScores(
        activePageScore(left, lens),
        activePageScore(right, lens),
        SORT_DIRECTION.ASC,
      )
    );
  }
  if (sort === PAGE_SORT_KEY.CLUSTER) {
    return (
      compareClusters(
        clusterKey(left.path, clusterContext),
        clusterKey(right.path, clusterContext),
      ) ||
      pageDisplayLabel(left, data.site).localeCompare(
        pageDisplayLabel(right, data.site),
        undefined,
        { sensitivity: "base" },
      )
    );
  }
  return pageDisplayLabel(left, data.site).localeCompare(
    pageDisplayLabel(right, data.site),
    undefined,
    { sensitivity: "base" },
  );
}

function activePageScore(
  page: OverviewResponse["pages"][number],
  lens: PageMapLens,
): number | null {
  return lens === PAGE_MAP_LENS.AGENT
    ? page.agentScore
    : pageContentFitnessScore(page);
}

function booleanFirst(left: boolean, right: boolean): number {
  return Number(right) - Number(left);
}

function healthRank(score: number | null): number {
  switch (pageHealthClass(score)) {
    case HEALTH_STATE.POOR:
      return 0;
    case HEALTH_STATE.WATCH:
      return 1;
    case HEALTH_STATE.GOOD:
      return 2;
    default:
      return 3;
  }
}

function compareScores(
  left: number | null,
  right: number | null,
  direction: SortDirection,
): number {
  if (left === null && right === null) {
    return 0;
  }
  if (left === null) {
    return 1;
  }
  if (right === null) {
    return -1;
  }
  return direction === SORT_DIRECTION.ASC ? left - right : right - left;
}

function compareClusters(left: string, right: string): number {
  return (
    clusterSortWeight(left) - clusterSortWeight(right) ||
    left.localeCompare(right)
  );
}

function clusterSortWeight(key: string): number {
  switch (key) {
    case "home":
      return 0;
    case "collections":
      return 1;
    case "products":
      return 2;
    case "guides":
      return 3;
    case "policies":
      return 4;
    case "support":
      return 5;
    case "checkout":
      return 6;
    default:
      return 7;
  }
}
